import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSiteContentValue, useTrackingSettings } from '@/hooks/useSiteContentValue';

// React 19 hoists <title>/<meta>/<link>/<script> rendered anywhere in the
// tree straight into <head> and cleans them up on unmount — no
// react-helmet or similar library needed. ProductDetailPage already used
// this pattern for its own per-product tags; this component generalizes
// it for every other (static-content) page.
const SITE_URL = 'https://rxk.thekseoul.com';
const DEFAULT_TITLE = 'Rx.K Seoul | K-Pharmacy Beauty for Bangladesh — 3-Step Skincare Prescription from Seoul, South Korea';
const DEFAULT_DESCRIPTION =
  "Bangladesh's first dermocosmetic K-Beauty brand. Pharmacist-recommended clinical ingredients. Hydro Booster, Intensive Repair Serum, Barrier Shield Cream. 3-Step Daily Treatment.";
const DEFAULT_IMAGE =
  'https://api.thekseoul.com/storage/v1/object/public/rxk-content/legacy/eea3e3e3-54e5-4b18-ad1a-7e7a0c25647f__3---.jpg';

interface SeoProps {
  /** Looks up seo.<pageKey>.title / seo.<pageKey>.description from the
   * admin-editable rxk_site_content table — used by every static page. */
  pageKey?: string;
  /** Explicit values win over the pageKey DB lookup — used by dynamic
   * pages (a specific product/article) where the title is per-item. */
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
  /** og:type — defaults to "website"; product pages pass "product". */
  ogType?: string;
  noindex?: boolean;
  jsonLd?: object | object[];
}

export function Seo({ pageKey, title, description, image, imageAlt, ogType, noindex, jsonLd }: SeoProps) {
  const { i18n } = useTranslation();
  const location = useLocation();

  const dbTitle = useSiteContentValue(pageKey ? `seo.${pageKey}.title` : '__none__', i18n.language);
  const dbDescription = useSiteContentValue(pageKey ? `seo.${pageKey}.description` : '__none__', i18n.language);
  const { gscVerification } = useTrackingSettings();

  const finalTitle = title || dbTitle || DEFAULT_TITLE;
  const finalDescription = description || dbDescription || DEFAULT_DESCRIPTION;
  const finalImage = image || DEFAULT_IMAGE;
  const canonicalUrl = `${SITE_URL}${location.pathname}`;
  const jsonLdList = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {gscVerification && <meta name="google-site-verification" content={gscVerification} />}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={finalImage} />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:type" content={ogType || 'website'} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={finalImage} />
      {jsonLdList.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </>
  );
}
